import { lazy, Suspense, useEffect, useState } from "react";
import Navbar from "./components/layout/Navbar";
import PromoBanner from "./components/layout/PromoBanner";
import Footer from "./components/layout/Footer";
import Toast from "./components/ui/Toast";
import ErrorBoundary from "./components/ui/ErrorBoundary";

// HomePage is the LCP path — load eagerly. Every other page is code-split.
import HomePage from "./pages/HomePage";

const ShopPage = lazy(() => import("./pages/ShopPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const DeliveryPage = lazy(() => import("./pages/DeliveryPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const WhyChooseUsPage = lazy(() => import("./pages/WhyChooseUsPage"));
const OurStudioPage = lazy(() => import("./pages/OurStudioPage"));
const GiftMessagesPage = lazy(() => import("./pages/GiftMessagesPage"));
const CustomOrdersPage = lazy(() => import("./pages/CustomOrdersPage"));
const GiftsPage = lazy(() => import("./pages/GiftsPage"));
const DeliveryZonesPage = lazy(() => import("./pages/DeliveryZonesPage"));
const FAQsPage = lazy(() => import("./pages/FAQsPage"));

// Interactive overlays — only load when user opens them.
const CartSidebar = lazy(() => import("./components/layout/CartSidebar"));
const AuthModal = lazy(() => import("./components/ui/AuthModal"));
const AccountPanel = lazy(() => import("./components/layout/AccountPanel"));
const ProductModal = lazy(() => import("./components/ui/ProductModal"));
const GiftPickerSheet = lazy(() => import("./components/ui/GiftPickerSheet"));
// Stripe.js (~240 KB) + Maps API are not on the critical path.
const CheckoutOverlay = lazy(
  () => import("./components/checkout/CheckoutOverlay"),
);

import { CartProvider, useCart } from "./context/CartContext";
import { AppProvider, useApp } from "./context/AppContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { PointsConfigProvider } from "./context/PointsConfigContext";

// Page-level Suspense fallback. Kept invisible so we don't flash a spinner
// during the tiny chunk fetch — pages typically resolve in <50ms after first load.
const PageFallback = () => (
  <div style={{ minHeight: "60vh" }} aria-hidden="true" />
);

function PageRouter() {
  const { page } = useApp();
  // HomePage is bundled with the main entry (LCP path). Other pages render
  // inside Suspense so their chunks load on demand.
  if (page === "home") {
    return (
      <main id="main-content" style={{ paddingTop: "var(--header-h, 64px)" }}>
        <HomePage />
      </main>
    );
  }
  return (
    <main id="main-content" style={{ paddingTop: "var(--header-h, 64px)" }}>
      <Suspense fallback={<PageFallback />}>
        {page === "shop" && <ShopPage />}
        {page === "about" && <AboutPage />}
        {page === "delivery" && <DeliveryPage />}
        {page === "contact" && <ContactPage />}
        {page === "why-choose-us" && <WhyChooseUsPage />}
        {page === "our-studio" && <OurStudioPage />}
        {page === "gift-messages" && <GiftMessagesPage />}
        {page === "custom-orders" && <CustomOrdersPage />}
        {page === "gifts" && <GiftsPage />}
        {page === "delivery-zones" && <DeliveryZonesPage />}
        {page === "faqs" && <FAQsPage />}
      </Suspense>
    </main>
  );
}

// When Afterpay redirects back, ?payment_intent=...&redirect_status=... are in the URL.
// CheckoutOverlay handles these params on its own mount — it opens itself, fetches the
// order from DB, and shows the confirmation screen. Nothing needed here.

// Handle Stripe 3DS redirect for billing setup: /?billing_setup=complete
// The setup_intent.succeeded webhook will have already fired and set monthly_billing_status=active.
// We just need to refresh the profile so the account panel reflects the new status.
function BillingSetupReturnHandler() {
  const { user, refreshProfile, setAccountPanelOpen } = useAuth();
  const [pending] = useState(
    () => new URLSearchParams(window.location.search).get("billing_setup") === "complete"
  );

  // Clear the param from URL once
  useEffect(() => {
    if (pending) window.history.replaceState({}, "", window.location.pathname);
  }, [pending]);

  // Wait for the session to load (refreshProfile is a no-op without a user),
  // then reopen the account panel and poll — the webhook may be slightly delayed.
  useEffect(() => {
    if (!pending || !user) return;
    setAccountPanelOpen?.(true);
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      await refreshProfile?.();
      if (attempts >= 8) clearInterval(poll);
    }, 800);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, user?.id]);
  return null;
}

// Watches the fixed site-header and keeps --header-h in sync so pages
// can use padding-top: var(--header-h) without hardcoding pixel values.
function HeaderHeightObserver() {
  useEffect(() => {
    const el = document.getElementById("site-header");
    if (!el) return;
    const update = () =>
      document.documentElement.style.setProperty(
        "--header-h",
        `${el.offsetHeight}px`,
      );
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return null;
}

// Only mount each overlay (and its deps) when the user actually opens it.
// This keeps Stripe.js, Google Maps, ProductModal etc. off the critical path.
function LazyCheckoutOverlay() {
  const { checkoutOpen, setCheckoutOpen } = useApp();
  if (!checkoutOpen) return null;
  return (
    <ErrorBoundary message="Something went wrong during checkout. Please try again." onReset={() => setCheckoutOpen(false)}>
      <Suspense fallback={null}>
        <CheckoutOverlay />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyCartSidebar() {
  const { cartOpen, setCartOpen } = useCart();
  if (!cartOpen) return null;
  return (
    <ErrorBoundary message="Something went wrong with the cart." onReset={() => setCartOpen(false)}>
      <Suspense fallback={null}>
        <CartSidebar />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyAuthModal() {
  const { authModalOpen, setAuthModalOpen } = useAuth();
  if (!authModalOpen) return null;
  return (
    <ErrorBoundary message="Something went wrong. Please try again." onReset={() => setAuthModalOpen(false)}>
      <Suspense fallback={null}>
        <AuthModal />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyAccountPanel() {
  const { accountPanelOpen, setAccountPanelOpen } = useAuth();
  if (!accountPanelOpen) return null;
  return (
    <ErrorBoundary message="Something went wrong loading your account." onReset={() => setAccountPanelOpen(false)}>
      <Suspense fallback={null}>
        <AccountPanel />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyProductModal() {
  const { modalProduct, setModalProduct } = useApp();
  if (!modalProduct) return null;
  return (
    <ErrorBoundary message="Something went wrong loading this product." onReset={() => setModalProduct(null)}>
      <Suspense fallback={null}>
        <ProductModal />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyGiftPickerSheet() {
  const { giftPickerCategory, setGiftPickerCategory } = useApp();
  if (!giftPickerCategory) return null;
  return (
    <ErrorBoundary message="Something went wrong." onReset={() => setGiftPickerCategory(null)}>
      <Suspense fallback={null}>
        <GiftPickerSheet />
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AuthProvider>
        <PointsConfigProvider>
          <WishlistProvider>
            <CartProvider>
              <HeaderHeightObserver />
              <BillingSetupReturnHandler />
              <div className="min-h-screen bg-brand-cream-50 overflow-x-hidden">
                <div
                  id="site-header"
                  className="fixed top-0 left-0 right-0 z-50"
                >
                  <PromoBanner />
                  <Navbar />
                </div>
                <PageRouter />
                <Footer />
                <LazyCartSidebar />
                <LazyCheckoutOverlay />
                <LazyAuthModal />
                <LazyAccountPanel />
                <LazyProductModal />
                <LazyGiftPickerSheet />
                <Toast />
              </div>
            </CartProvider>
          </WishlistProvider>
        </PointsConfigProvider>
      </AuthProvider>
    </AppProvider>
  );
}
